import React, { useState, useEffect, useRef } from 'react'
import { Anomaly, AnomalyStats } from '../../types/anomaly'
import AnomalyChart from '../../components/AI/AnomalyChart'
import AnomalyTable from '../../components/AI/AnomalyTable'
import AnomalyFilters from '../../components/AI/AnomalyFilters'

const AnomalyDetection: React.FC = () => {
  const [stats, setStats] = useState<AnomalyStats | null>(null)
  const [anomalies, setAnomalies] = useState<Anomaly[]>([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({})
  const wsRef = useRef<WebSocket | null>(null)

  useEffect(() => {
    fetchStats()
    fetchAnomalies()
    connectWebSocket()
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close()
      }
    }
  }, [])

  const fetchStats = async () => {
    try {
      // Mock data - replace with actual API call
      const mockStats: AnomalyStats = {
        counts: [
          { severity: 'critical', count: 3 },
          { severity: 'warning', count: 12 },
          { severity: 'info', count: 8 }
        ],
        unacked_total: 15
      }
      setStats(mockStats)
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    }
  }

  const fetchAnomalies = async () => {
    try {
      setLoading(true)
      // Mock data - replace with actual API call
      const mockAnomalies: Anomaly[] = Array.from({ length: 20 }, (_, i) => ({
        id: i + 1,
        timestamp: new Date(Date.now() - i * 3600000).toISOString(),
        system_id: `INV-${String(i % 5 + 1).padStart(2, '0')}`,
        metric: 'power_kw',
        value: Math.random() * 100 + 10,
        expected: Math.random() * 100 + 50,
        score: Math.random() * 10 + 1,
        severity: ['info', 'warning', 'critical'][Math.floor(Math.random() * 3)] as 'info' | 'warning' | 'critical',
        context: { telemetry_id: 12345 + i },
        features: {
          power_kw: Math.random() * 100 + 10,
          irradiance: Math.random() * 800 + 200,
          temperature: Math.random() * 15 + 20
        },
        explanation: {
          irradiance: Math.random() * 2 - 1,
          temperature: Math.random() * 2 - 1
        },
        acknowledged: Math.random() > 0.7,
        acknowledged_by: Math.random() > 0.7 ? 'operator1' : undefined,
        created_at: new Date(Date.now() - i * 3600000).toISOString()
      }))
      setAnomalies(mockAnomalies)
    } catch (error) {
      console.error('Failed to fetch anomalies:', error)
    } finally {
      setLoading(false)
    }
  }

  const connectWebSocket = () => {
    // Disable WebSocket connection for now
    console.log('WebSocket connection disabled - using mock data only')
    return
  }

  const handleAcknowledge = async (id: number, acknowledgedBy: string) => {
    try {
      // Mock API call - replace with actual implementation
      setAnomalies(prev => 
        prev.map(a => 
          a.id === id 
            ? { ...a, acknowledged: true, acknowledged_by: acknowledgedBy, acknowledged_at: new Date().toISOString() }
            : a
        )
      )
      fetchStats()
    } catch (error) {
      console.error('Failed to acknowledge anomaly:', error)
    }
  }

  const handleFilterChange = (newFilters: any) => {
    setFilters(newFilters)
    // Apply filters and refetch data
    fetchAnomalies()
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Anomaly Detection</h1>
        <div className="flex gap-4">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Retrain Models
          </button>
          <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
            Export Report
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Unacknowledged</h3>
          <p className="text-2xl font-bold text-red-600">{stats?.unacked_total || 0}</p>
        </div>
        {stats?.counts.map((count, index) => (
          <div key={count.severity} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 capitalize">
              {count.severity} Anomalies
            </h3>
            <p className={`text-2xl font-bold ${
              count.severity === 'critical' ? 'text-red-600' :
              count.severity === 'warning' ? 'text-yellow-600' :
              'text-blue-600'
            }`}>
              {count.count}
            </p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <AnomalyFilters onFilterChange={handleFilterChange} />

      {/* Chart */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Anomaly Timeline</h3>
        <AnomalyChart anomalies={anomalies.slice(0, 100)} />
      </div>

      {/* Table */}
      <AnomalyTable 
        anomalies={anomalies} 
        loading={loading}
        onAcknowledge={handleAcknowledge}
      />
    </div>
  )
}

export default AnomalyDetection